import { useState } from 'react';
import { Link } from 'react-router-dom';
import { products } from '../../mocks/products';
import ProductCard from './components/ProductCard';
import NutritionPanel from './components/NutritionPanel';
import PackagingOptions from './components/PackagingOptions';

const categories = ['All', 'Fruits', 'Vegetables', 'Custom Blends'];

export default function ProductsPage() {
  const [activeCategory, setActiveCategory] = useState('All');
  const [selectedId, setSelectedId] = useState(products[0].id);

  const filtered = activeCategory === 'All'
    ? products
    : products.filter((p) => p.category === activeCategory);

  const selected = products.find((p) => p.id === selectedId) ?? products[0];

  return (
    <div className="min-h-screen bg-[#faf9f7]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 cursor-pointer">
            <img
              src="https://static.readdy.ai/image/1ae6d47aa9f5caec142554cc0371daf1/04683b963241abfa25e88f4f0de2b164.jpeg"
              alt="Freeze Dried Philippines Logo"
              className="w-10 h-10 object-contain"
            />
            <span className="text-lg font-bold text-gray-900 whitespace-nowrap">FREEZE DRIED PHILIPPINES</span>
          </Link>
          <div className="flex items-center gap-8">
            <Link to="/" className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer">Home</Link>
            <Link to="/products" className="text-sm font-bold text-amber-700 border-b-2 border-amber-600 pb-0.5 whitespace-nowrap cursor-pointer">Products</Link>
            <a href="/#about" className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer">About</a>
            <a href="/#pricing" className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer">Pricing</a>
            <a href="/#contact" className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer">Contact</a>
            <a
              href="/#contact"
              className="bg-amber-700 text-white text-sm font-semibold px-5 py-2.5 rounded-lg hover:bg-amber-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              Get a Quote
            </a>
          </div>
        </div>
      </nav>

      {/* Hero Banner */}
      <section className="relative pt-20">
        <div className="w-full h-64 overflow-hidden">
          <img
            src="https://public.readdy.ai/ai/img_res/5b185c512a47f397616c902c19ef79d8.jpg"
            alt="Product Specifications Banner"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/30 to-transparent" style={{top: '80px'}}></div>
        </div>
        <div className="absolute inset-0 flex items-center" style={{top: '80px'}}>
          <div className="max-w-7xl mx-auto px-6 w-full">
            <p className="text-amber-300 text-sm font-semibold uppercase tracking-widest mb-2">Full Catalog</p>
            <h1 className="text-5xl font-bold text-white mb-3">Product Specifications</h1>
            <p className="text-white/80 text-lg max-w-xl">
              Detailed nutritional information, packaging options, and pricing for all our freeze-dried products.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="max-w-7xl mx-auto px-6 py-14">

        {/* Category Filter */}
        <div className="flex items-center gap-3 mb-10">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-5 py-2 rounded-full text-sm font-semibold transition-all whitespace-nowrap cursor-pointer ${
                activeCategory === cat
                  ? 'bg-amber-700 text-white shadow-md'
                  : 'bg-white text-gray-600 border border-gray-200 hover:border-amber-400 hover:text-amber-700'
              }`}
            >
              {cat}
            </button>
          ))}
          <span className="ml-auto text-sm text-gray-400">{filtered.length} product{filtered.length !== 1 ? 's' : ''} found</span>
        </div>

        <div className="flex gap-10 items-start">
          {/* Product Grid */}
          <div className="w-[420px] flex-shrink-0">
            <div className="grid grid-cols-2 gap-4">
              {filtered.map((product) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  isSelected={selectedId === product.id}
                  onClick={() => setSelectedId(product.id)}
                />
              ))}
            </div>
          </div>

          {/* Detail Panel */}
          <div className="flex-1 min-w-0 sticky top-24">
            {/* Product Header */}
            <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 mb-6">
              <div className="w-full h-64 overflow-hidden">
                <img
                  src={selected.image}
                  alt={selected.name}
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-6">
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div>
                    <span className="text-xs font-semibold uppercase tracking-widest text-amber-700 bg-amber-50 px-3 py-1 rounded-full">
                      {selected.category}
                    </span>
                    <h2 className="text-3xl font-bold text-gray-900 mt-3">{selected.name}</h2>
                    <p className="text-gray-500 text-sm mt-1">Origin: <strong className="text-gray-700">{selected.origin}</strong></p>
                  </div>
                  <div className="flex flex-wrap gap-2 justify-end">
                    {selected.tags.map((tag) => (
                      <span key={tag} className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-md whitespace-nowrap">{tag}</span>
                    ))}
                  </div>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed mb-4">{selected.description}</p>
                <div className="flex items-start gap-2 bg-amber-50 rounded-xl px-4 py-3">
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-settings-3-line text-amber-600 text-base"></i>
                  </div>
                  <p className="text-xs text-amber-800 leading-relaxed">
                    <strong>Process:</strong> {selected.process}
                  </p>
                </div>
              </div>
            </div>

            {/* Quality Badges */}
            <div className="grid grid-cols-4 gap-3 mb-6">
              {[
                { icon: 'ri-leaf-line', label: '100% Natural', sub: 'No additives' },
                { icon: 'ri-shield-check-line', label: 'FDA Registered', sub: 'Certified safe' },
                { icon: 'ri-temp-cold-line', label: 'Cold Chain', sub: 'Preserved fresh' },
                { icon: 'ri-time-line', label: '18–24 Months', sub: 'Shelf life' },
              ].map((badge) => (
                <div key={badge.label} className="bg-white rounded-xl border border-gray-100 p-4 text-center shadow-sm">
                  <div className="w-8 h-8 flex items-center justify-center mx-auto mb-2 text-amber-600">
                    <i className={`${badge.icon} text-xl`}></i>
                  </div>
                  <p className="text-xs font-bold text-gray-800">{badge.label}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{badge.sub}</p>
                </div>
              ))}
            </div>

            {/* Nutrition + Packaging */}
            <div className="grid grid-cols-2 gap-6">
              <NutritionPanel product={selected} />
              <PackagingOptions product={selected} />
            </div>

            {/* CTA */}
            <div className="mt-6 bg-gradient-to-r from-amber-800 to-amber-700 rounded-2xl p-6 flex items-center justify-between gap-6">
              <div>
                <h4 className="text-white font-bold text-lg">Interested in {selected.name}?</h4>
                <p className="text-amber-200 text-sm mt-1">Request a bulk quote or order a sample pack today.</p>
              </div>
              <div className="flex gap-3 flex-shrink-0">
                <a
                  href="/#contact"
                  className="bg-white text-amber-800 text-sm font-bold px-5 py-3 rounded-xl hover:bg-amber-50 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Request Quote
                </a>
                <a
                  href="/#contact"
                  className="bg-amber-900/60 text-white text-sm font-semibold px-5 py-3 rounded-xl hover:bg-amber-900/80 transition-colors whitespace-nowrap cursor-pointer border border-amber-600"
                >
                  Order Sample
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-amber-950 py-10 mt-10">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src="https://static.readdy.ai/image/1ae6d47aa9f5caec142554cc0371daf1/04683b963241abfa25e88f4f0de2b164.jpeg"
              alt="Freeze Dried Philippines"
              className="w-8 h-8 object-contain"
            />
            <span className="text-white font-bold text-sm">FREEZE DRIED PHILIPPINES</span>
          </div>
          <p className="text-amber-300/60 text-xs">© 2025 Freeze Dried Philippines. All rights reserved.</p>
          <div className="flex items-center gap-6">
            <Link to="/" className="text-amber-300/70 text-sm hover:text-white transition-colors cursor-pointer">Home</Link>
            <a href="/#pricing" className="text-amber-300/70 text-sm hover:text-white transition-colors cursor-pointer">Pricing</a>
            <a href="/#contact" className="text-amber-300/70 text-sm hover:text-white transition-colors cursor-pointer">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
